import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Settings } from "lucide-react";
import { Button, EmptyState, NoticeBox, SectionHeader, Tabs } from "../../ui";
import { fetchCopilotMetaAPI } from "../../../utils/api/copilot";
import { fetchCopilotSettingsAPI, updateAutoReplySettingsAPI } from "../../../utils/api/copilotAdmin";
import AutoReplySettings from "./AutoReplySettings";
import { GeneralSettings, NotificationSettings } from "./GeneralNotificationSettings";
import FaqSettings from "./FaqSettings";
import SavedRepliesSettings from "./SavedRepliesSettings";
import { SETTINGS_TEXT, settingsError } from "./copilotSettingsText";
import "./CopilotSettings.css";
import { useConfirmedAction } from "../useConfirmedAction";

export const SETTINGS_TABS = ["general", "auto", "replies", "faqs", "notifications"];

// Settings of LUD Copilot: general facts, the automatic first replies, the saved replies and the
// user's own notification switches. Everything here reads and writes the real backend; what a
// role may change is decided by `access` here for the UI and again by the server.
export default function CopilotSettings({ lang, tab, onTab, onBack, access, agentName }) {
  const confirmation = useConfirmedAction(lang);
  const t = SETTINGS_TEXT[lang] || SETTINGS_TEXT.en;
  const rtl = lang === "ar";
  const BackIcon = rtl ? ArrowRight : ArrowLeft;
  const [state, setState] = useState({ status: "loading", settings: null, error: null });
  const [categories, setCategories] = useState(null);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);
  const noticeTimer = useRef(null);
  const active = SETTINGS_TABS.includes(tab) ? tab : "general";

  const flash = useCallback((value) => {
    setNotice(value);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 5000);
  }, []);
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: prev.settings ? "ready" : "loading", error: null }));
    try {
      const data = await fetchCopilotSettingsAPI();
      setState({ status: "ready", settings: data.settings, error: null });
    } catch (error) {
      setState((prev) => ({ ...prev, status: prev.settings ? "ready" : "error", error: settingsError(error, t) }));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchCopilotMetaAPI().then((data) => setCategories(data.categories), () => {}); }, []);

  // Confirmation serializes settings writes; the server answer is the new truth.
  const update = (key, body, success) => {
    const resetting = Object.values(body.templates || {}).some((languages) => Object.values(languages).includes(""));
    return confirmation.ask(async () => {
      setBusy(key);
      try {
        const data = await updateAutoReplySettingsAPI(body);
        setState({ status: "ready", settings: data.settings, error: null });
        flash({ type: "success", text: success });
        return true;
      } catch (error) {
        flash({ type: "error", text: settingsError(error, t) });
        return false;
      } finally {
        setBusy(null);
      }
    }, key === "enabled" ? "toggle" : resetting ? "reset" : "save", t.auto.title,
    resetting || (key === "enabled" && !body.enabled),
    key === "enabled" ? `${body.enabled ? t.on : t.off}. ${t.auto.appliesToNew}` : undefined);
  };

  const { settings } = state;
  return (
    <div className={`cs-root ${rtl ? "rtl" : ""}`} dir={rtl ? "rtl" : "ltr"}>
      <header className="cs-header">
        <SectionHeader icon={Settings} title={t.title} description={t.subtitle} />
        <Button variant="neutral" appearance="outline" icon={<BackIcon size={16} />} onClick={onBack}>{t.backToInbox}</Button>
      </header>

      <Tabs idPrefix="cs" label={t.tabsLabel} rtl={rtl} value={active} onChange={onTab} tabs={SETTINGS_TABS.map((key) => ({ key, label: t.tabs[key] }))} />

      {notice && (
        <div className="cs-toast" role={notice.type === "error" ? "alert" : "status"}>
          <NoticeBox variant={notice.type === "error" ? "danger" : "success"} size="sm">{notice.text}</NoticeBox>
        </div>
      )}

      <div className="cs-panel" role="tabpanel" id={`cs-panel-${active}`} aria-labelledby={`cs-tab-${active}`} tabIndex={0}>
        {active === "notifications" ? (
          <NotificationSettings t={t} lang={lang} />
        ) : state.status === "loading" ? (
          <EmptyState variant="loading" compact>{t.loading}</EmptyState>
        ) : state.status === "error" ? (
          <EmptyState variant="error" action={<Button size="sm" variant="neutral" appearance="outline" onClick={load}>{t.retry}</Button>}>
            {t.loadError} {state.error}
          </EmptyState>
        ) : active === "general" ? (
          <GeneralSettings t={t} lang={lang} settings={settings} canManageAuto={access.manageSettings} onOpenTab={onTab} />
        ) : active === "auto" ? (
          <AutoReplySettings
            t={t}
            lang={lang}
            settings={settings}
            categories={categories}
            canManage={access.manageSettings}
            busy={busy}
            onToggle={(enabled) => update("enabled", { enabled }, enabled ? t.auto.switchedOn : t.auto.switchedOff)}
            onSaveTemplate={(category, language, text) => update(`${category}.${language}`, { templates: { [category]: { [language]: text } } }, t.auto.savedTemplate)}
            onResetTemplate={(category, language) => update(`${category}.${language}`, { templates: { [category]: { [language]: "" } } }, t.auto.resetDone)}
          />
        ) : active === "faqs" ? (
          <FaqSettings t={t} lang={lang} canManage={access.manageFaqs} onNotice={flash} />
        ) : (
          <SavedRepliesSettings t={t} lang={lang} categories={categories || settings?.categories} canManage={access.manageReplies} agentName={agentName} onNotice={flash} />
        )}
      </div>
      {confirmation.dialog}
    </div>
  );
}
